import React, {
  useState,
  useRef,
  useImperativeHandle,
  forwardRef,
  useMemo,
} from "react";
import { Loading } from "@xp-design/mobile";
import FontSetBox from "@/component/fontSetBox";
import { RightOutline } from "antd-mobile-icons";
import PopupCom from "@/component/popupCom";
import OneTwoThreeBox from "../oneTwoThreeBox";
import RankList from "../rankList";
import { getCurrentUid } from "@/api/common";
import { individualLeaderboard, teamLeaderboard } from "../../ajax";
import { UserActivityData, IndividualLeaderboard } from "../../type";
import parentStyle from "../../index.module.less";
import style from "./index.module.less";
import { enhancedSafeChainFind } from "../../../../utils";
import PrizeRecordPopup, {
  PrizeRecordPopupRef,
} from "@/components/PrizeRecordPopup";
import { getChannel } from "@/utils/tools";
import { trackGdp } from "@/utils/gio-report";

interface FullRankingPopupProps {
  moduleJsonData: any;
  activityCode: string;
  boxBackUrl: string;
  isPeoModel: boolean;
  rankObj: any;
  isNewBigRanking: boolean;
  activityInfo: any;
  maxPageSize: number;
}

export interface FullRankingPopupRef {
  show: () => void;
}

const FullRankingPopup = forwardRef<FullRankingPopupRef, FullRankingPopupProps>(
  (
    {
      moduleJsonData,
      activityCode,
      boxBackUrl,
      isPeoModel,
      rankObj,
      isNewBigRanking,
      activityInfo,
      maxPageSize,
    },
    ref,
  ) => {
    const popupRef = useRef<any>(null);
    const [maskShow, setMaskShow] = useState(false);

    // 中奖记录弹窗引用
    const prizeRecordPopupRef = useRef<PrizeRecordPopupRef>(null);
    // 样式数据相关的useMemo
    const updateTimeStyleData = useMemo(() => {
      const result = enhancedSafeChainFind(moduleJsonData.visual)
        ?.prizeSetting("fontSetting")
        ?.content("fontStyle")
        ?.options("updateTime")?.val?.styleData;
      return (
        result ||
        moduleJsonData?.visual?.prizeSetting?.[3]?.content?.[0]?.options?.[4]
          ?.styleData
      );
    }, [moduleJsonData]);

    const visualStyleTypeName = useMemo(() => {
      const result = enhancedSafeChainFind(moduleJsonData.visual)?.visualData(
        "visualStyle",
      )?.val?.typeName;
      return result || moduleJsonData?.visual?.visualData?.[0]?.typeName;
    }, [moduleJsonData]);
    // 弹窗专用的分页状态
    const [pageNo, setPageNo] = useState(1);
    const [rankDataList, setRankDataList] = useState<UserActivityData[]>([]);
    const [topThree, setTopThree] = useState<UserActivityData[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [isHaveData, setIsHaveData] = useState(true);

    // 暴露给父组件的方法
    useImperativeHandle(ref, () => ({
      show: showPopup,
    }));

    // 数据加载函数
    const getRankingListData = async (flag: boolean, pageIndex?: number) => {
      try {
        setLoading(true);
        Loading.show("加载中...");
        const uid = await getCurrentUid();
        const leaderboardFunction = flag
          ? individualLeaderboard
          : teamLeaderboard;

        const { records, total: totalCount } = (await leaderboardFunction({
          pageNo: pageIndex || pageNo,
          pageSize: 20, // 固定pageSize为20
          uid,
          activityCode,
        })) as IndividualLeaderboard;

        const currentPageNo = pageIndex || pageNo;

        setTotal(totalCount);

        if (totalCount == 0) {
          setIsHaveData(false);
        }

        if (currentPageNo === 1) {
          // 第一页，替换数据并处理前三名
          if (records.length >= 3) {
            // 处理前三名数据（重排序：第二名、第一名、第三名）
            const topThreeData = records.slice(0, 3);
            const rearranged = [
              topThreeData[1],
              topThreeData[0],
              topThreeData[2],
            ];
            setTopThree(rearranged);
            // 从第4名开始的数据作为列表数据，但要确保不超过maxPageSize
            const remainingQuota = Math.max(0, maxPageSize - 3);
            setRankDataList(records.slice(3, 3 + remainingQuota));
          } else {
            // 不足3个人，直接显示，但要确保不超过maxPageSize
            const validRecords = records.slice(0, maxPageSize);
            setTopThree(validRecords);
            setRankDataList([]);
          }
        } else {
          // 后续页，追加数据，但要确保不超过maxPageSize
          setRankDataList((prevArray) => {
            const existingCount = prevArray.length + topThree.length;
            const remainingQuota = Math.max(0, maxPageSize - existingCount);
            const newRecords = records.slice(0, remainingQuota);
            return [...prevArray, ...newRecords];
          });
        }

        setPageNo(currentPageNo + 1);

        // 检查是否还有更多数据可加载，同时确保不超过maxPageSize
        const currentTotalItems =
          currentPageNo === 1
            ? topThree.length + rankDataList.length
            : rankDataList.length + records.length;

        if (
          currentPageNo * 20 >= totalCount ||
          currentTotalItems >= maxPageSize
        ) {
          setIsHaveData(false);
        }
      } catch (error) {
        console.log("%c FullRankingPopup error", "color:#b03734", error);
      } finally {
        Loading.hide();
        setLoading(false);
      }
    };

    // 显示弹窗
    const showPopup = () => {
      // 重置状态
      setPageNo(1);
      setRankDataList([]);
      setTopThree([]);
      setTotal(0);
      setIsHaveData(true);

      setMaskShow(true);
      popupRef.current?.onOpen();

      // 加载数据
      getRankingListData(isPeoModel, 1);
    };

    // 关闭回调
    const onCloseCallBack = async () => {
      setTimeout(() => {
        setMaskShow(false);
        // 清理数据
        setPageNo(1);
        setRankDataList([]);
        setTopThree([]);
        setTotal(0);
        setIsHaveData(true);
      }, 500);
    };

    //我的奖品
    const myAward = () => {
      trackGdp("P20189", "B004", {
        trackName: "充电_营销专题页_组件点击",
        origin: getChannel(),
        userType: "",
        versionId: activityInfo.versionCode,
        pageId: activityInfo.pageCode,
        ComponentCode: activityInfo.moduleId,
        type: activityInfo.moduleType,
        activityId: activityInfo.connectedActivitiesId,
        comp_style: visualStyleTypeName,
        status: rankObj.activityStatusCn || "",
        clickLocation: "中奖记录",
      });
      // 使用弹窗显示中奖记录
      prizeRecordPopupRef.current?.show(activityCode);
    };

    return (
      <>
        <PopupCom
          ref={popupRef}
          height="80%"
          bacUrl={boxBackUrl}
          onCloseCallBack={onCloseCallBack}
          title={`${isPeoModel ? "个人" : "组队"}排行榜单`}
          children={
            <div className={style["fullRankingContainer"]}>
              {/* 更新时间和我的奖品 */}
              <div className={style["timeBox"]}>
                <FontSetBox
                  content={`${rankObj.activityStatus === "4" ? "" : "排行更新时间："}${rankObj.rankUpdateTime}`}
                  styleData={updateTimeStyleData}
                />
                <div onClick={() => myAward()}>
                  我的奖品
                  <RightOutline />
                </div>
              </div>

              {/* 前三名 */}
              <div className={style["topThreeSection"]}>
                <OneTwoThreeBox
                  list={topThree}
                  moduleJsonData={moduleJsonData}
                  isPeoModel={isPeoModel}
                />
              </div>

              {/* 完整排名列表 */}
              <RankList
                showNum={rankDataList.length}
                isShowMoreBtn={
                  isHaveData &&
                  total > rankDataList.length + 3 && // +3是因为前三名不在列表中
                  rankDataList.length + 3 < maxPageSize && // 确保总数不超过maxPageSize
                  !loading
                }
                isNotMore={
                  (total == rankDataList.length + 3 && !loading) || !isHaveData
                }
                list={rankDataList}
                moduleJsonData={moduleJsonData}
                isPeoModel={isPeoModel}
                getMore={getRankingListData}
                isNewBigRanking={isNewBigRanking}
                isInPopup={true}
                total={total}
              />
            </div>
          }
        />
        {maskShow && <div className={parentStyle.prizeMask}></div>}
        {/* 中奖记录弹窗组件 */}
        <PrizeRecordPopup
          ref={prizeRecordPopupRef}
          moduleJsonData={moduleJsonData}
          activityInfo={activityInfo}
        />
      </>
    );
  },
);

FullRankingPopup.displayName = "FullRankingPopup";

export default FullRankingPopup;
